import { useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-router';
import { last } from 'lodash-es';
import { theme } from '@frontend/theme';
import {
  useFormField,
  TextField,
  SearchIcon,
  ComboboxBase,
  usePopoverMenu,
  Text,
  PopoverMenuItem,
  CaretRightIconSmall,
  styles,
} from '@frontend/design-system';
import { useGlobalNavItems, useMainNavItems, type NavLink } from '../side-nav/use-nav-items';

type SearchItemParent = { label: string; parent?: SearchItemParent };
type SearchItem = { label: string; to: string; parent?: SearchItemParent };

const flattenNavItems = (navItems: NavLink[], parent?: SearchItemParent): SearchItem[] => {
  return navItems.flatMap((nav) => {
    const navParent = { label: nav.label, parent };
    const currentItem = nav.to ? [{ label: nav.label, to: nav.to, parent }] : [];
    const nestedItems = nav.subnav ? flattenNavItems(nav.subnav, navParent) : [];

    return [...currentItem, ...nestedItems];
  });
};

export const NavSearch = () => {
  const navigate = useNavigate();
  const mainNavItems = useMainNavItems();
  const globalNavItems = useGlobalNavItems();

  const flattenedNavItems = useMemo(() => {
    return [...flattenNavItems(mainNavItems), ...flattenNavItems(globalNavItems)];
  }, [mainNavItems, globalNavItems]);

  const sectionRef = useRef<HTMLElement | null>(null);
  const { onChange: searchFieldOnChange, onFocus, onBlur, ...searchParams } = useFormField({ type: 'text' });
  const { getMenuProps, getItemProps, getTriggerProps, open, close, activeIndex, setActiveIndex, refs } =
    usePopoverMenu<HTMLInputElement>({
      placement: 'bottom',
      middlewareOptions: {
        offset: {
          mainAxis: 9,
          crossAxis: -12,
        },
      },
      interactionOptions: { listNavigation: { virtual: true }, typeahead: { enabled: false } },
    });
  const [focused, setFocused] = useState(false);

  const searchedRoutes = useMemo(() => {
    const searchValue = (searchParams.value ?? '').toLowerCase();
    return flattenedNavItems.filter(
      (route) => (route.label ?? '').toLowerCase().includes(searchValue) || route.to.toLowerCase().includes(searchValue)
    );
  }, [flattenedNavItems, searchParams.value]);

  const { ref, ...triggerProps } = getTriggerProps({
    onKeyDown: (event) => {
      if (event.key === 'Enter' && activeIndex != null) {
        navigate({
          to: searchedRoutes[activeIndex]?.to,
        });
        setActiveIndex(null);
        close();
      }
    },
    onChange: (e) => {
      searchFieldOnChange(e);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore our change events are still messed up :(
      if (e.target.value) {
        open();
      } else {
        close();
      }
    },
  });

  const getComboboxListContainerWidth = sectionRef.current ? sectionRef.current?.getBoundingClientRect().width : 200;
  const searchInputTextColor = focused ? theme.colors.neutral90 : theme.colors.neutral20;

  return (
    <section
      css={[
        css`
          padding: ${theme.spacing(3)};
          border-bottom: 1px solid ${theme.colors.neutral60};
        `,
      ]}
    >
      <ComboboxBase
        menuProps={getMenuProps()}
        menuStyles={css`
          width: ${getComboboxListContainerWidth}px;
          border-radius: 0 0 ${theme.borderRadius.small} ${theme.borderRadius.small};
          padding-bottom: 0;
          max-height: 400px !important;
          overflow-y: auto;
          padding-top: 0;
        `}
        Input={
          <section ref={sectionRef}>
            <TextField
              aria-autocomplete='list'
              role='combobox'
              startAdornment={<SearchIcon />}
              {...triggerProps}
              containerCss={css`
                background: ${focused ? theme.colors.white : theme.colors.neutral80};
                border-radius: ${theme.borderRadius.small};
                transition: 0.2s ease;
                caret-color: ${searchInputTextColor};

                svg {
                  fill: ${searchInputTextColor};
                }

                & input {
                  color: ${searchInputTextColor} !important;
                }
                & input::placeholder {
                  color: ${searchInputTextColor};
                }
              `}
              fieldComponentProps={{ ref: refs.setReference }}
              name=''
              label=''
              placeholder='Search'
              onFocus={(e) => {
                setFocused(true);
                onFocus(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur(e);
              }}
              {...searchParams}
            />
          </section>
        }
      >
        {searchedRoutes.map((item, index) => {
          return (
            <PopoverMenuItem
              key={item.to}
              {...getItemProps({
                index,
                onClick: () => {
                  navigate({ to: item.to });
                  setActiveIndex(null);
                  close();
                },
              })}
              css={css`
                height: auto;
                padding: ${theme.spacing(1, 2)};
                border-bottom: 1px solid ${theme.colors.neutral10};
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-flow: row;
                  justify-content: space-between;
                  gap: ${theme.spacing(1)};
                  align-items: center;
                  width: 100%;
                  cursor: pointer;
                `}
              >
                <NavSearchItem item={item} />
              </div>
            </PopoverMenuItem>
          );
        })}
      </ComboboxBase>
    </section>
  );
};

const NavSearchBreadcrumbItem = ({ crumb, isLastItem }: { crumb: string; isLastItem: boolean }) => (
  <>
    <span
      css={css`
        color: ${isLastItem ? 'black' : theme.colors.neutral50};
        flex: 0 1 auto !important;
      `}
      aria-hidden='true'
    >
      {crumb}
    </span>
    {!isLastItem && <CaretRightIconSmall size={8} aria-hidden='true' />}
  </>
);

export const NavSearchItem = ({ item }: { item: SearchItem }) => {
  const breadcrumb = item.label.split('/');
  const parent = item.parent?.label;
  return (
    <>
      <div
        role='text'
        aria-label={`Path: ${item.to}`}
        css={css`
          width: 100%;
          position: relative;
        `}
      >
        <Text textAlign='left' css={styles.truncate}>
          {last(breadcrumb)}
        </Text>
        <div
          css={css`
            display: flex;
            align-items: center;
            width: 100%;
            gap: ${theme.spacing(1)};
            font-size: 12px;
            justify-content: flex-start;
          `}
        >
          {parent && (
            <>
              <NavSearchBreadcrumbItem crumb={parent} isLastItem={false} />
              <NavSearchBreadcrumbItem crumb={item.label} isLastItem={true} />
            </>
          )}
        </div>
      </div>
    </>
  );
};
